import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Popover, Checkbox, FormControlLabel, Divider, TextField, Typography, Link } from "@mui/material";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import { IosShare, FilterList } from "@mui/icons-material";
import { Order } from "@/interfaces";
import { OrderStatus } from "@/enums";
import { baseUrl, orderStatuses } from "@/consts";
import { formatCurrency, formatName, isNumber } from "@/utils";
import { MuiHelper } from "@/helpers";
import { colors, sxButton, sxFont_12_500 } from "@/theme";
import { DebounceInput } from "@/components/atoms";
import { CrudGrid } from "@/components/organisms";
import { dateValueGetter } from "@/components/organisms/CrudGrid/helpers";

const orderStatusTag = (order?: Order) => (
  <Box
    sx={{
      ...sxFont_12_500,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "72px",
      height: "30px",
      borderRadius: "20px",
      color:
        order?.status === OrderStatus.PAID
          ? colors.mutedTealGreen
          : order?.status === OrderStatus.CANCELLED
            ? colors.white
            : colors.neutralGrey800,
      backgroundColor:
        order?.status === OrderStatus.PAID
          ? colors.lightGreen
          : order?.status === OrderStatus.CANCELLED
            ? colors.accent3Error
            : colors.neutralGrey300,
    }}
  >
    {orderStatuses[order?.status || 0]}
  </Box>
);

const orderStatusRender = (order?: Order) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
    }}
  >
    {orderStatusTag(order)}
  </Box>
);

const columns: GridColDef[] = [
  {
    field: "orderId",
    headerName: "Order",
    width: 100,
    renderCell: (params) => (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <div>#{params.value}{params.row.offsite && <span style={{ color: "blue", marginLeft: 8, fontSize: 14 }}>OFF-SITE</span>} </div>
      </div>
    ),
  },
  {
    field: "dateTimeCreated",
    headerName: "Date",
    headerAlign: "center",
    align: "center",
    width: 100,
    type: "date",
    valueGetter: dateValueGetter,
    // renderCell: (params) => {
    //   const date = params.value;
    //   if (!date) return null;
      
    //   return (
    //     <div style={{ textAlign: "center" }}>
    //       <div>{formatDateWithTimezone(date)}</div>
    //     </div>
    //   );
    // },
  },
  {
    field: "lastName",
    headerName: "Customer name",
    headerAlign: "center",
    align: "center",
    width: 200,
    valueGetter: (_, row) => formatName(row.firstName, row.lastName),
  },
  {
    field: "dealerIdentifier",
    headerName: "Badge ID",
    headerAlign: "center",
    align: "center",
    width: 100,
  },
  {
    field: "masterDealerId",
    headerName: "Master ID",
    headerAlign: "center",
    align: "center",
    width: 100,
  },
  {
    field: "coopUsed",
    headerName: "Co-Op Used",
    flex: 1,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "creditCardUsed",
    headerName: "Credit Card",
    width: 100,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "discount",
    headerName: "Discount",
    width: 100,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "couponUsed",
    headerName: "Coupon",
    width: 100,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "offsite",
    headerName: "Offsite",
    width: 100,
    type: "boolean",
  },
  {
    field: "shippingFee",
    headerName: "Shipping",
    width: 100,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "total",
    headerName: "Total cost",
    flex: 1,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "status",
    headerName: "Status",
    headerAlign: "center",
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => orderStatusRender(params.row),
  },
];

const getRowId = (row: Order) => row.orderId;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setRowId = (row: any, id: any) => {
  row.orderId = id;
};

// FiltersPopover component
function FiltersPopover() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [status, setStatus] = useState({ paid: false, saved: false });
  const [shipping, setShipping] = useState<{ min: number | string; max: number | string }>({ min: 0, max: 0 });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: rect.bottom,
      left: rect.left - 150, // shift 100px to the left
    });
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleStatusChange = (name: 'paid' | 'saved') => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setStatus({ paid: name === 'paid', saved: name === 'saved' });
    } else {
      setStatus({ paid: false, saved: false });
    }
  };
  const handleShippingChange = (name: 'min' | 'max') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    let newValue = value === '' ? '' : Number(value);

    if (name === 'min') {
      let newMin = newValue;
      let newMax = shipping.max;
      if (newMin !== '' && newMax !== '' && Number(newMin) > Number(newMax)) {
        newMax = newMin;
      }
      setShipping({ min: newMin, max: newMax });
    } else {
      let newMax = newValue;
      let newMin = shipping.min;
      if (newMax !== '' && newMin !== '' && Number(newMax) < Number(newMin)) {
        newMin = newMax;
      }
      setShipping({ min: newMin, max: newMax });
    }
  };
  const handleResetPrice = () => {
    setShipping({ min: 1, max: 50 });
  };
  const handleClearAll = () => {
    setStatus({ paid: false, saved: false });
    setShipping({ min: 0, max: 0 });
  };
  const handleApply = () => {
    // Implement filter logic here
    handleClose();
  };

  return (
    <>
      <Button
        variant="text"
        sx={{
          color: '#2C3856',
          fontFamily: 'PF DIN Text Pro',
          fontSize: 16,
          textTransform: 'none',
          px: 1,
          py: 1,
          minWidth: 0,
          fontWeight: 400,
          gap: 1,
        }}
        startIcon={<FilterList sx={{ fontSize: 28, ml: 0.5 }} />}
        onClick={handleOpen}
      >
        Filters
      </Button>
      <Popover
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || { top: 0, left: 0 }}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 3,
            minWidth: 300,
            maxWidth: 340,
            p: 0,
          }
        }}
      >
        <Box sx={{ p: 3, pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#2C3856', mb: 1 }}>
            Filter by Status
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', pl:1, gap:2 }}>
            <FormControlLabel
              control={<Checkbox checked={status.paid} onChange={handleStatusChange('paid')} sx={{ p: 0 }} />}
              label={<Typography sx={{ fontSize: 16, color: '#2C3856', fontWeight: 500 }}>Paid</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={status.saved} onChange={handleStatusChange('saved')} sx={{ p: 0}} />}
              label={<Typography sx={{ fontSize: 16, color: '#2C3856', fontWeight: 500 }}>Saved</Typography>}
            />
          </Box>
        </Box>
        <Divider />
        <Box sx={{ p: 3, pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#2C3856', mb: 1 }}>
            Filter by Shipping Fee
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <TextField
              value={shipping.min}
              onChange={handleShippingChange('min')}
              size="small"
              variant="outlined"
              sx={{ width: '40%', background: '#F7F8FA', borderRadius: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ color: '#A0AEC0', fontWeight: 600, fontSize: 16, mr: 0.5 }}>$</Typography>,
                sx: { fontWeight: 600, fontSize: 16, color: '#2C3856', pl: 0.5 }
              }}
            />
            <Typography sx={{ color: '#A0AEC0', fontWeight: 600, fontSize: 18 }}>—</Typography>
            <TextField
              value={shipping.max}
              onChange={handleShippingChange('max')}
              size="small"
              variant="outlined"
              sx={{ width: '40%', background: '#F7F8FA', borderRadius: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ color: '#A0AEC0', fontWeight: 600, fontSize: 16, mr: 0.5 }}>$</Typography>,
                sx: { fontWeight: 600, fontSize: 16, color: '#2C3856', pl: 0.5 }
              }}
            />
          </Box>
          <Box sx={{width:'100%',display:'flex', alignItems:'center', justifyContent:'center', mt:2}}>
            <Link component="button" underline="none" sx={{ color: '#1A7FF7', fontWeight: 500, fontSize: 16, cursor: 'pointer', mb: 1, alignSelf: 'center',fontFamily:'PF DIN Text Pro' }} onClick={handleResetPrice}>
              Reset Price Range
            </Link>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, pt: 2 }}>
          <Button variant="text" sx={{ color: '#2C3856', fontWeight: 500, fontSize: 16, textTransform: 'none' }} onClick={handleClearAll}>
            Clear all
          </Button>
          <Button variant="contained" sx={{ background: '#1A7FF7', color: '#fff', fontWeight: 600, fontSize: 16, borderRadius: 2, px: 4, boxShadow: 'none', textTransform: 'none', '&:hover': { background: '#1769c2' } }} onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}

const Orders: React.FC = () => {
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  const getAllOrdersWithDealerEndPoint = "withdealer";

  const handleRowClick: GridEventListener<"rowClick"> = (params) => {
    if (!isNumber(params?.id as number)) {
      return;
    }

    navigate(`${params?.id}`);
  };

  const exportFn = async () => {
    try {
      setIsExporting(true);
      const now = new Date();
      const endDate = now.toISOString().replace('T', ' ').slice(0, 19);
      const startDate = '2025-06-02 20:12:00'; // Fixed start date from the provided URL
      
      const url = `${process.env.REACT_APP_REST_BASE_URL}/Orders/export?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}&offsite=false&custom=true`;
      console.log(url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();
      console.log('Export response:', data);
      
      // Create a blob from the CSV data
      const blob = new Blob([data], { type: 'text/csv' });
      
      // Create a temporary URL for the blob
      const url2 = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const a = document.createElement('a');
      a.href = url2;
      a.download = `orders_export_${new Date().toISOString().slice(0, 10)}.csv`; // filename with date
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url2);
      
      console.log('CSV file downloaded successfully');
      
    } catch (error) {
      console.error('Error exporting orders:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: 'center', justifyContent: 'space-between', p: 4 }}>
        {MuiHelper.getHeader("Orders")}
        {/* <Button sx={{ px: 3, py: 1.5, fontFamily: 'PF DIN Text Pro', backgroundColor: colors.primaryOzone500, color: colors.white, fontSize: 16, borderRadius: 2 }}>Add Order</Button> */}
      </Box>
      <Box sx={{ backgroundColor: colors.neutralGrey300, padding: 3, width:'auto' }}>
        <CrudGrid
          baseUrl={`${baseUrl.ORDER}/${getAllOrdersWithDealerEndPoint}`}
          columns={columns}
          getRowId={getRowId}
          setRowId={setRowId}
          fieldToFocus="orderIdentifier"
          initialState={{
            sorting: {
              sortModel: [{ field: "orderIdentifier", sort: "desc" }],
            },
          }}
          onRowClick={handleRowClick}
          search={search}
          withoutActionColumn={true}
          exportButton={{
            text: 'Export',
            endIcon: <IosShare />,
            onClick: exportFn,
            isLoading: isExporting,
            sxButton: sxButton
          }}
          filtersButton={<FiltersPopover />}
        />
      </Box>
    </Box>
  );
};

export default Orders;
